import { useEffect } from "react";
import { STATUS_COLORS } from "../constants";

// ─── Toast ───────────────────────────────────────────────────────────────────
export const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{
      position: "fixed", top: 24, right: 24, zIndex: 9999,
      background: type === "success" ? "#0f172a" : "#7f1d1d",
      color: "#fff", padding: "14px 20px", borderRadius: 12,
      fontSize: 14, fontFamily: "'DM Sans', sans-serif",
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      borderLeft: `4px solid ${type === "success" ? "#10b981" : "#ef4444"}`,
      animation: "slideIn 0.3s ease", maxWidth: 320,
    }}>
      {type === "success" ? "✓" : "✕"} {message}
    </div>
  );
};

// ─── Badge ───────────────────────────────────────────────────────────────────
export const Badge = ({ status }) => {
  const c = STATUS_COLORS[status] || { bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" };
  return (
    <span style={{
      background: c.bg, color: c.text, padding: "3px 10px",
      borderRadius: 20, fontSize: 12, fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 5,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, display: "inline-block" }} />
      {status}
    </span>
  );
};

// ─── Modal ───────────────────────────────────────────────────────────────────
export const Modal = ({ children, onClose, title }) => (
  <div
    onClick={e => e.target === e.currentTarget && onClose()}
    style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      backdropFilter: "blur(4px)",
    }}
  >
    <div style={{
      background: "#fff", borderRadius: 20, width: "100%", maxWidth: 620,
      maxHeight: "90vh", overflow: "auto",
      boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
    }}>
      <div style={{
        padding: "20px 24px", borderBottom: "1px solid #f1f5f9",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        position: "sticky", top: 0, background: "#fff", zIndex: 1,
      }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{title}</h3>
        <button onClick={onClose} style={{
          background: "#f1f5f9", border: "none", borderRadius: 8,
          width: 32, height: 32, cursor: "pointer", fontSize: 16, color: "#64748b",
        }}>✕</button>
      </div>
      <div style={{ padding: 24 }}>{children}</div>
    </div>
  </div>
);

// ─── Form Controls ────────────────────────────────────────────────────────────
const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 };
const inputBaseStyle = {
  width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0",
  borderRadius: 10, fontSize: 14, color: "#0f172a", background: "#f8fafc",
  outline: "none", boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif",
};

export const Input = ({ label, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={labelStyle}>{label}</label>}
    <input style={inputBaseStyle} {...props} />
  </div>
);

export const Select = ({ label, children, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={labelStyle}>{label}</label>}
    <select style={inputBaseStyle} {...props}>{children}</select>
  </div>
);

export const Textarea = ({ label, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={labelStyle}>{label}</label>}
    <textarea style={{ ...inputBaseStyle, resize: "vertical", minHeight: 80 }} {...props} />
  </div>
);

// ─── Button ───────────────────────────────────────────────────────────────────
export const Btn = ({ children, variant = "primary", size = "md", ...props }) => {
  const styles = {
    primary:   { background: "#0f172a", color: "#fff",     border: "none" },
    secondary: { background: "#f1f5f9", color: "#374151",  border: "none" },
    danger:    { background: "#fee2e2", color: "#991b1b",  border: "none" },
    success:   { background: "#d1fae5", color: "#065f46",  border: "none" },
    outline:   { background: "transparent", color: "#0f172a", border: "1.5px solid #e2e8f0" },
  };
  const sizes = {
    sm: { padding: "6px 12px",  fontSize: 12 },
    md: { padding: "9px 18px",  fontSize: 14 },
    lg: { padding: "12px 24px", fontSize: 15 },
  };
  return (
    <button
      style={{
        ...styles[variant], ...sizes[size],
        borderRadius: 10, fontWeight: 600, cursor: "pointer",
        fontFamily: "'DM Sans', sans-serif", transition: "opacity 0.15s",
        whiteSpace: "nowrap",
      }}
      onMouseOver={e => (e.currentTarget.style.opacity = "0.82")}
      onMouseOut={e =>  (e.currentTarget.style.opacity = "1")}
      {...props}
    >
      {children}
    </button>
  );
};